import React from 'react';
import { ChevronRightIcon, SyncIcon } from '@primer/octicons-react';
import { LTText, LTTooltip } from '@lambdatestincprivate/lt-components';
import { browserDisplayName, formatDurationMs, osDisplayName, osLabel, toBrowserKey, toOSKey, truncateMiddle } from '../format';
import { BrowserIcon } from '../icons/BrowserIcon';
import { ClockIcon } from '../icons/ClockIcon';
import { OSIcon } from '../icons/OSIcon';
import { StatusIcon } from '../icons/StatusIcon';
import styles from './instances.module.css';
import { jsxs as _jsxs, jsx as _jsx, Fragment as _Fragment } from "react/jsx-runtime";
function MetaItem(_ref) {
  var icon = _ref.icon,
    label = _ref.label,
    srPrefix = _ref.srPrefix,
    tooltip = _ref.tooltip;
  var content = /*#__PURE__*/_jsxs("span", {
    className: styles.metaItem,
    children: [srPrefix && /*#__PURE__*/_jsxs("span", {
      className: "ev-sr-only",
      children: [srPrefix, ": "]
    }), icon, /*#__PURE__*/_jsx(LTText, {
      text: label,
      variant: LTText.TextVariant.SMALL_REGULAR,
      color: "var(--lt-text-secondary)"
    })]
  });
  if (!tooltip) return content;
  return /*#__PURE__*/_jsx(LTTooltip, {
    text: tooltip,
    direction: "bottom",
    customTriggerStyle: {
      display: 'inline-flex'
    },
    children: content
  });
}
var Separator = function Separator() {
  return /*#__PURE__*/_jsx("span", {
    className: styles.metaDot,
    "aria-hidden": "true"
  });
};

/**
 * One test-instance row: status icon, title, then the dot-separated meta
 * line (testcase id, OS, browser, duration) — the Test Manager row, fed
 * from result.yaml instead of an API. Loaded rows are buttons opening the
 * detail view; pending/error rows stay inert.
 */
export var InstanceRow = /*#__PURE__*/React.memo(function InstanceRow(_ref2) {
  var _row$env, _row$env2, _row$env3, _row$env4, _row$attempts$length, _row$attempts;
  var row = _ref2.row,
    onSelect = _ref2.onSelect;
  if (row.state === 'pending') {
    return /*#__PURE__*/_jsxs("div", {
      className: styles.rowInner,
      children: [/*#__PURE__*/_jsx("span", {
        className: "ev-sr-only",
        children: "Loading test details"
      }), /*#__PURE__*/_jsx("span", {
        className: styles.skeletonIcon,
        "aria-hidden": "true"
      }), /*#__PURE__*/_jsxs("div", {
        className: styles.rowBody,
        "aria-hidden": "true",
        children: [/*#__PURE__*/_jsx("span", {
          className: "".concat(styles.skeletonBar, " ").concat(styles.skeletonTitle)
        }), /*#__PURE__*/_jsx("span", {
          className: "".concat(styles.skeletonBar, " ").concat(styles.skeletonMeta)
        })]
      })]
    });
  }
  if (row.state === 'error') {
    return /*#__PURE__*/_jsxs("div", {
      className: styles.rowInner,
      children: [/*#__PURE__*/_jsx(StatusIcon, {
        status: "unknown"
      }), /*#__PURE__*/_jsxs("div", {
        className: styles.rowBody,
        children: [/*#__PURE__*/_jsx(LTText, {
          text: row.id,
          variant: LTText.TextVariant.SUBHEADER_BOLD,
          color: "var(--lt-text-primary)",
          className: styles.rowTitle
        }), /*#__PURE__*/_jsx(LTText, {
          text: "Couldn't read this test's result".concat(row.errorMessage ? " \u2014 ".concat(row.errorMessage) : ''),
          variant: LTText.TextVariant.SMALL_REGULAR,
          color: "var(--lt-text-secondary)"
        })]
      })]
    });
  }
  var meta = [];
  if (row.testcaseId) {
    var _row$tcId;
    meta.push(/*#__PURE__*/_jsx(MetaItem, {
      label: (_row$tcId = row.tcId) !== null && _row$tcId !== void 0 ? _row$tcId : truncateMiddle(row.testcaseId, 15),
      tooltip: row.testcaseId,
      srPrefix: "Test case"
    }, "tc"));
  }
  var os = toOSKey((_row$env = row.env) === null || _row$env === void 0 ? void 0 : _row$env.os);
  var osText = osLabel((_row$env2 = row.env) === null || _row$env2 === void 0 ? void 0 : _row$env2.os, (_row$env3 = row.env) === null || _row$env3 === void 0 ? void 0 : _row$env3.osVersion);
  if (osText) {
    // The icon is aria-hidden, so the OS/browser NAME must reach screen
    // readers as text — a bare "14.5" is meaningless without "macOS".
    meta.push(/*#__PURE__*/_jsx(MetaItem, {
      icon: /*#__PURE__*/_jsx(OSIcon, {
        os: os,
        size: 16
      }),
      label: osText,
      srPrefix: osDisplayName(os)
    }, "os"));
  }
  if ((_row$env4 = row.env) !== null && _row$env4 !== void 0 && _row$env4.browser) {
    var _row$env$browserVersi;
    var browser = toBrowserKey(row.env.browser);
    meta.push(/*#__PURE__*/_jsx(MetaItem, {
      icon: /*#__PURE__*/_jsx(BrowserIcon, {
        browser: browser,
        size: 16
      }),
      label: (_row$env$browserVersi = row.env.browserVersion) !== null && _row$env$browserVersi !== void 0 ? _row$env$browserVersi : row.env.browser,
      srPrefix: browserDisplayName(browser)
    }, "browser"));
  }
  if (row.durationMs !== undefined) {
    meta.push(/*#__PURE__*/_jsx(MetaItem, {
      icon: /*#__PURE__*/_jsx(ClockIcon, {
        size: 16
      }),
      label: formatDurationMs(row.durationMs),
      srPrefix: "Duration"
    }, "duration"));
  }
  // result.yaml's authored chronological attempts list — more than one
  // entry means the test was retried; earlier attempts are viewable from
  // the detail header's switcher.
  var retries = ((_row$attempts$length = (_row$attempts = row.attempts) === null || _row$attempts === void 0 ? void 0 : _row$attempts.length) !== null && _row$attempts$length !== void 0 ? _row$attempts$length : 0) - 1;
  if (retries > 0) {
    meta.push(/*#__PURE__*/_jsx(MetaItem, {
      icon: /*#__PURE__*/_jsx(SyncIcon, {
        size: 14,
        "aria-hidden": "true"
      }),
      label: "".concat(retries, " ").concat(retries === 1 ? 'retry' : 'retries'),
      srPrefix: "Retried"
    }, "retries"));
  }
  var body = /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(StatusIcon, {
      status: row.status
    }), /*#__PURE__*/_jsxs("div", {
      className: styles.rowBody,
      children: [/*#__PURE__*/_jsx(LTText, {
        text: row.title,
        variant: LTText.TextVariant.SUBHEADER_BOLD,
        color: "var(--lt-text-primary)",
        className: styles.rowTitle,
        title: row.title
      }), /*#__PURE__*/_jsx("span", {
        className: styles.metaLine,
        children: meta.map(function (item, i) {
          return /*#__PURE__*/_jsxs(React.Fragment, {
            children: [i > 0 && /*#__PURE__*/_jsx(Separator, {}), item]
          }, i);
        })
      })]
    })]
  });
  if (!onSelect) return /*#__PURE__*/_jsx("div", {
    className: styles.rowInner,
    children: body
  });
  return /*#__PURE__*/_jsx("button", {
    type: "button"
    // The focus-restore target when the detail view closes.
    ,
    id: "ev-instance-row-".concat(row.id),
    className: styles.rowButton,
    onClick: function onClick() {
      return onSelect(row.id);
    },
    children: /*#__PURE__*/_jsxs("span", {
      className: styles.rowInner,
      children: [body, /*#__PURE__*/_jsx("span", {
        className: styles.rowChevron,
        "aria-hidden": "true",
        children: /*#__PURE__*/_jsx(ChevronRightIcon, {
          size: 16
        })
      })]
    })
  });
});